/**
 * Core utilities
 */

import Radio from "backbone.radio";
import validator from "validator";
import numberFormat from "number-format.js";
import platform from "platform";
import cookieParser from "strict-cookie-parser";

// Standard english stop words taken from Lucene's StopAnalyzer
const STOPWORDS = [
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "but",
  "by",
  "for",
  "if",
  "in",
  "into",
  "is",
  "it",
  "no",
  "not",
  "of",
  "on",
  "or",
  "such",
  "that",
  "the",
  "their",
  "then",
  "there",
  "these",
  "they",
  "this",
  "to",
  "was",
  "will",
  "with",
];
const SINGLE_HYPHEN = "-";
// See http://momentjs.com/docs/
export const OUTPUT_DATE_FORMAT = "MM/DD/YYYY";
export const TIMESTAMP_DATE_FORMAT = `${OUTPUT_DATE_FORMAT} hh:mm:ss A`;
export const INPUT_DATE_FORMAT = "M/D/YYYY";

export const trimText = (text) => validator.trim(text);

export const getRouteFromLocation = () => routeFromHash(document.location.hash);

/**
 * Create an ID-free version of a route name by replacing groups of capitalized
 * strings and numbers with asterisks. This name can be used to find the correct
 * DT button configuration and persist column customizations.
 *
 * @param {String} name - An original route name, with IDs, warts and all.
 */
export const generalizeRouteName = (name = "") => {
  return name ? replaceString(name, /([A-Z0-9])+/g, "*") : "";
};

/**
 * Converts document.location.hash or path to an RVC route.
 * @param {String} hash
 */
export const routeFromHash = (hash = "") => {
  const path = hash.replace("#/", "");
  const route = replaceString(path, /\//g, ".");
  return route;
};

/**
 * Converts an RVC route to a document.location.hash.
 * @param {String} route
 */
export const hashFromRoute = (route = "") => {
  const path = replaceString(route, /\./g, "/");
  return `#/${path}`;
};

/**
 * Creates a base-64 encoded ASCII string from a String object.
 * @param {String} ascii
 */
export const encodeData = (ascii = "") => btoa(ascii) || "";

/**
 * Creates an ASCII string from a base64 encoded String object.
 * @param {base64} base64
 */
export const decodeData = (base64 = "") => atob(base64) || "";

/**
 * Returns the value of a cookie by its name, or undefined when not found. When
 * a name is not passed, a map of all available cookies is returned.
 * @param {String} name (optional)
 * @returns {String|Map}
 */
export const getCookies = (name = null) => {
  const cookies = cookieParser.parseCookieHeader(document.cookie);
  return cookies ? (name ? cookies.get(name) : cookies) : undefined;
};

/**
 * custom handler for unfetch since it won't reject on HTTP error status
 * @param {xhrResponse} response
 */
export const checkFetchStatus = (response) => {
  if (response.ok) {
    return response;
  } else {
    var error = new Error(response.statusText);
    error.response = response;
    return Promise.reject(error);
  }
};

/**
 * Creates an instance of Moment from the passed in date string. Input date
 * must meet the application's default format, m/d/y
 * @param {String} value
 * @param {String} defaultFormat
 * @param {Boolean} strictMode
 * @returns {Moment}
 */
export const dateAsMoment = (
  value,
  defaultFormat = INPUT_DATE_FORMAT,
  strictMode = false
) => {
  try {
    const m = moment(value, defaultFormat, strictMode);
    return m.isValid() ? m : null;
  } catch (err) {
    console.log(err.stack);
    return null;
  }
};

/**
 * Validates a String date
 * @param {String} value
 * @returns {Boolean}
 */
export const isValidDate = (value, format = INPUT_DATE_FORMAT) => {
  const mDate = dateAsMoment(value, format);
  return (mDate && mDate._isValid) || false;
};

/**
 * Returns a unix millisecond timestamp from a String date
 * @param {String} value
 * @returns {Number}
 */
export const dateInMilliseconds = (value, format = INPUT_DATE_FORMAT) => {
  const mDate = dateAsMoment(value, format);
  return (mDate && mDate.format("x")) || undefined;
};

/**
 * Returns the difference between date1 with date2
 * See: https://momentjs.com/docs/#/displaying/difference/
 * @param {Date} date1
 * @param {Date} date2
 * @param {String} measurement [ms|seconds|minutes|hours|days|weeks|months|years]
 * @param {String} format
 * @returns {Number}
 */
export const dateDifference = (
  date1,
  date2,
  measurement = "ms",
  format = INPUT_DATE_FORMAT
) => {
  const m1 = dateAsMoment(date1, format);
  const m2 = dateAsMoment(date2, format);
  if (m1 && m1._isValid && m2 && m2._isValid) {
    return m1.diff(m2, measurement);
  } else {
    return undefined;
  }
};

/**
 * Checks whether a date falls within a range of dates, inclusive of those dates.
 * @param {Date} value
 * @param {Date} between1
 * @param {Date} between2
 * @param {String} format
 * @returns {Boolean}
 */
export const isDateBetween = (
  value,
  between1,
  between2,
  format = INPUT_DATE_FORMAT
) => {
  const mDate = dateAsMoment(value, format);
  const mBetween1Date = dateAsMoment(between1, format);
  const mBetween2Date = dateAsMoment(between2, format);
  if (
    mDate &&
    mDate._isValid &&
    mBetween1Date &&
    mBetween1Date._isValid &&
    mBetween2Date &&
    mBetween2Date._isValid
  ) {
    return mDate.isBetween(mBetween1Date, mBetween2Date, null, "()");
  } else {
    return undefined;
  }
};

/**
 * Compares whether date1 is the same or before date2
 * See: https://momentjs.com/docs/#/query/is-same-or-before/
 * @param {Date} date1
 * @param {Date} date2
 * @param {String} granularity [second|minute|hour|day|week|month|year]
 * @param {String} format
 */
export const isDateSameOrBefore = (
  date1,
  date2,
  granularity = "day",
  format = INPUT_DATE_FORMAT
) => {
  const m1 = dateAsMoment(date1, format);
  const m2 = dateAsMoment(date2, format);
  if (m1 && m1._isValid && m2 && m2._isValid) {
    if (m1 && m1._isValid && m2 && m2._isValid) {
      return m1.isSameOrBefore(m2, granularity);
    } else {
      return undefined;
    }
  }
};

/**
 * Compares whether date1 is the same or after date2
 * See: https://momentjs.com/docs/#/query/is-same-or-after/
 * @param {Date} date1
 * @param {Date} date2
 * @param {String} granularity [second|minute|hour|day|week|month|year]
 * @param {String} format
 */
export const isDateSameOrAfter = (
  date1,
  date2,
  granularity = "day",
  format = INPUT_DATE_FORMAT
) => {
  const m1 = dateAsMoment(date1, format);
  const m2 = dateAsMoment(date2, format);
  if (m1 && m1._isValid && m2 && m2._isValid) {
    return m1.isSameOrAfter(m2, granularity);
  } else {
    return undefined;
  }
};

/**
 * Formats a date using the moment JS library
 * @param {Date} value
 * @param {String} outputFormat
 * @param {moment.ISO_8601} inputFormat
 * @returns {String}
 */
export const dateFormatter = (
  value,
  outputFormat = OUTPUT_DATE_FORMAT,
  inputFormat = moment.ISO_8601
) => {
  // See http://momentjs.com/docs/
  return moment(value, inputFormat).format(outputFormat);
};

/**
 * Uses moment to convert a unix timestamp to a proper Date and runs it through
 * the default formatter.
 * @param {Long} value
 * @returns {String}
 */
export const unixTimestampFormatter = (value) => {
  if (!_.isNumber(value)) {
    return "";
  }

  const dt = moment(value).format();
  return dateFormatter(dt, TIMESTAMP_DATE_FORMAT);
};

/**
 * Formats a date using the moment JS library using the date relative variation
 * @param {Date} value
 * @returns {String}
 */
export const relativeDateFormatter = (value) => {
  // See http://momentjs.com/docs/
  return moment(value).fromNow();
};

/**
 * Escapes HTML markup
 * @param {String} value
 * @returns {String}
 */
export const htmlEscape = (value) => {
  // escapes HTML code to entities.
  return value ? validator.escape(value) : "";
};

/**
 * Unescapes HTML markup
 * @param {String} value
 * @returns {String}
 */
export const htmlUnescape = (value) => {
  // escapes HTML code to entities.
  return value ? validator.unescape(value) : "";
};

/**
 * Formats numbers using javascript-number-formatter
 * @param {Numeric} value
 * @param {String} mask
 * @returns {String} formatted number without decimal places
 */
export const numberFormatter = (value, mask) => {
  mask = mask || "#,##0.";
  // See https://code.google.com/p/javascript-number-formatter/
  return numberFormat(mask, value);
};

/**
 * Formats numbers using javascript-number-formatter
 * @param {Numeric} value
 * @param {String} mask
 * @returns {String} formatted number with 2 decimal places
 */
export const floatFormatter = (value, mask) => {
  mask = mask || "#,##0.00";
  // See https://code.google.com/p/javascript-number-formatter/
  return numberFormat(mask, value);
};

/**
 * Formats numbers using javascript-number-formatter
 * @param {Numeric} value
 * @param {String} mask
 * @returns {String} currency formatted number with 2 decimal places
 */
export const currencyFormatter = (value, mask) => {
  mask = mask || "$#,##0.00";
  // See https://code.google.com/p/javascript-number-formatter/
  return numberFormat(mask, value);
};

/**
 * Creates a human readable string denoting a range of values
 * @param {Array} values
 * @param {String} separator
 * @returns {String}
 */
export const rangeFormatter = (values, separator) => {
  separator = separator || " to ";
  // make sure only numbers are processed
  const numList = values.filter((value) => parseInt(value, 10));
  // make sure list if not empty
  if (!_.isEmpty(numList)) {
    return numList.join(separator);
  } else {
    return "";
  }
};

// Adapted from DataTables ellipsis plugin:
// See https://github.com/DataTables/Plugins/blob/master/dataRender/ellipsis.js
export const ellipsis = (
  d,
  cutoff = 15,
  wordbreak = true,
  escapeHtml = true
) => {
  if (typeof d !== "number" && typeof d !== "string") {
    return d;
  }

  d = d.toString(); // cast numbers

  if (d.length <= cutoff) {
    return d;
  }

  let shortened = d.substr(0, cutoff - 1);

  // Find the last white space character in the string
  if (wordbreak) {
    shortened = replaceString(shortened, /\s([^\s]*)$/, "");
  }

  // Protect against uncontrolled HTML input
  if (escapeHtml) {
    shortened = validator.escape(shortened);
  }

  return `${shortened}&#8230;`;
};

/* Parses an array of objects into an object of name/value pairs
 * @param {Array} a
 * @returns {Object} json
 */
export const arrayToObject = (a) => {
  const o = {};
  $.each(a, function () {
    // if exists
    if (o[this.name]) {
      // if it is not an array...
      if (!o[this.name].push) {
        // convert its current value to an array
        o[this.name] = [o[this.name]];
      }
      // push new value into it
      o[this.name].push(this.value || "");
    } else {
      o[this.name] = this.value || "";
    }
  });
  return o;
};

/**
 * Normalizes form fields with multiple values into multiple name/value pairs
 * @param {Array} a
 * @returns {Array}
 */
export const normalizeFormArray = (a) => {
  const o = [];
  let valueArray;
  _.each(a, (item) => {
    if (valueArray.length > 1) {
      // convert
      _.each(valueArray, (value) => o.push({ name: item.name, value }));
    } else {
      // return as-is
      o.push(item);
    }
  });
  return o;
};

/**
 * Determines the center position of a floating element, like a modal window
 * @param {Selector} selector
 * @returns {Boolean|Object}
 */
export const findCenterPosition = (selector) => {
  const win = $(window),
    el = $(selector),
    pos = {};
  if (win && el) {
    pos._meta = {};
    pos._meta.toTop = win.scrollTop();
    pos._meta.winH = win.innerHeight();
    pos._meta.winW = win.innerWidth();
    pos._meta.elH = el.outerHeight();
    pos._meta.elW = el.outerWidth();
    pos._meta.winCenter = {
      top: Math.round(pos._meta.winH / 2),
      left: Math.round(pos._meta.winW / 2),
    };
    pos.top =
      pos._meta.toTop + pos._meta.winCenter.top - Math.round(pos._meta.elH / 2);
    pos.left = pos._meta.winCenter.left - Math.round(pos._meta.elW / 2);
    return pos;
  }
  return false;
};

/**
 * Walks the DOM tree to identify the passed element"s parent node
 * matching the specified class name. The immediate parent node will be
 * returned when a parent class name is not provided.
 *
 * @param {Element/Selector} el
 * @param {String} parentClassName
 * @returns jQuery element
 */
export const getParentNode = (el, parentClassName) => {
  return parentClassName ? el.parents(parentClassName) : el.parent();
};

/*
 * @param {String} paramName
 *
 * Returns either:
 * - the value of the named param name passed; or
 * - an object literal of all params if a named param is not passed.
 */
export const getQueryParams = (paramName) => {
  // get params from URL
  let params = window.location.search.substring(1);
  params = params ? params.split("&") : [];

  // create empty output object
  const qp = {};

  // loop thru params
  params.forEach((param) => {
    // split name from value
    const parts = decodeURIComponent(param).split("=");

    // does param name exist? (multiple values)
    if (qp[parts[0]]) {
      // is current value a string?
      if (typeof qp[parts[0]] === "string") {
        // copy existing value
        const temp = qp[parts[0]];

        // convert to array and add existing value
        qp[parts[0]] = [temp];
      }

      // add current value
      qp[parts[0]].push(parts[1]);
    } else {
      // set single value
      qp[parts[0]] = parts[1];
    }
  });

  // return requested param name
  if (paramName) {
    return qp[paramName];
  }

  // return complete output object
  return qp;
};

/**
 * Verifies text is a string and then removes everything but alphanumerics,
 * blank spaces and hyphens. Then trims final output.
 *
 * @param {String} str
 * @returns String
 */
export const sanitizeString = (text, regex = "[^A-Za-z0-9- &+]+") => {
  return trimText(text.replace(new RegExp(regex, "g"), ""));
};

/**
 * Casts a sanitized string into an integer if possible, or NaN
 *
 * @param {String} str
 * @returns Int
 */
export const sanitizeInt = (str) => {
  return validator.toInt(sanitizeString(str));
};

/**
 *
 * @param {String} target - HTML element
 * @param {Function} callback - Optional callback to execute once animation completes
 * @returns {void}
 */
export const scrollToElement = (target, callback) => {
  const cb = callback || emptyFn;
  const topoffset = target === "html" ? 0 : 30;
  const speed = 750;
  const destination = $(target).offset().top - topoffset;
  $("html:not(:animated),body:not(:animated)").animate(
    { scrollTop: destination },
    speed,
    cb
  );
};

/**
 * Returns a random integer between min (included) and max (excluded)
 *
 * @param {Number} min
 * @param {Number} max
 * @returns {Number}
 */
export const getRandomInt = (min = 1, max = 1000000) => {
  return Math.floor(Math.random() * (max - min)) + min;
};

/**
 * Creates a globally unique ID with an optional custom prefix
 *
 * @param {String} prefix
 */
export const getUniqueId = (prefix = "el-") => {
  return _.uniqueId(prefix);
};

/**
 * Creates a DOM element of the passed in type (defaults to "div"),
 * className (defaults to "hide") and appends it to the current HTML
 * document.
 *
 * @param {String} type
 * @param {String} className
 * @returns {String} id
 */
export const createElement = (type = "div", className = "hide") => {
  // create a globally-unique ID
  const id = getUniqueId("rvc-");

  // make sure the ID does not already exist
  if ($(`#${id}`).length === 0) {
    // define element
    const $el = $(`<${type}/>`, { id }).addClass(className);

    // append element to the body
    $("body").append($el);

    // return the newly created ID
    return `#${id}`;
  }

  return null;
};

/**
 * Creates an "invisible" iframe and appends it to the current HTML document.
 *
 * @returns {String} id
 */
export const createIFrame = () => {
  // create container element
  const id = createElement("iframe");

  // return the newly created ID
  return id;
};

/**
 * Creates a Bootstrap modal and appends it to the current HTML document.
 *
 * @param {Object} options
 * @returns {String} id
 */
export const createModal = (
  options = { scrollable: true, large: false, tagAsGenerated: true }
) => {
  // configure core class names
  const classNames = ["modal", "fade"];

  if (!!options.tagAsGenerated) {
    classNames.push("generated-modal");
  }

  // apply non scrollable class as necessary
  !!!options.scrollable && classNames.push("no-scroll");

  // create container element
  const id = createElement("div", classNames.join(" "));

  // define content elements
  const $child = $(`
		<div class="modal-dialog">
			<div class="modal-content">
				<div class="modal-header">
					<button type="button" class="close" data-dismiss="modal" aria-label="Close window">
						<i class="fa fa-fw fa-times-circle" aria-hidden="true"></i>
					</button>
					<h4 class="modal-title"></h4>
				</div>
				<div class="modal-body">
					<div class="loading-indicator"></div>
				</div>
				<div class="modal-footer">
					<div class="actions">
						<!-- role-based user actions here -->
					</div>
					<div class="form-buttons">
						<!-- default dismiss button, can be overriden by view implementation -->
						<div class="pull-right">
							<button type="reset" data-dismiss="modal" data-event-category="Drilldown" data-event-action="Close" class="btn btn-sm btn-link">Dismiss</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	`);

  // apply large modal class when necessary
  !!options.large && $child.addClass("modal-lg");

  // select new container element and inject content
  $(id).append($child);

  // return the newly created ID
  return id;
};

/**
 * Converts a string to a URL safe slug. Pass true to eliminate common
 * words from the slug.
 *
 * @param {String} text
 * @param {Boolean} removeStopwords
 * @returns {String}
 */
export const slugify = (text, removeStopwords) => {
  const prettified = trimText(text);
  const o = prettified
    .toLowerCase() // Camel case is bad
    .replace(/#/, "no") // replace # with text equiv
    .replace(/%/, "pct") // replace % with text equiv
    .replace(/[/]+/g, " ") // replace slashes with spaces
    .replace(/[^a-z0-9_\-~!+\s]+/g, "") // Exchange invalid chars
    .trim()
    .replace(/[\s]+/g, SINGLE_HYPHEN); // Swap whitespace for single hyphen
  return removeStopwords ? _removeStopwords(o) : o;
};

/**
 * Wraps a String replace function.
 *
 * @param {String} input
 * @param {String|Regex} pattern
 * @param {String|Function} replacement
 */
export const replaceString = (input, ...args) => {
  const USAGE = "You must pass 3 arguments: input, pattern and replacement";

  if ("string" !== typeof input || input === "") throw Error(USAGE);

  if (args.length !== 2) throw Error(USAGE);

  // extract expected params from args
  let pattern = args[0];
  const replacement = args[1];

  // convert non string patters to regex
  if ("object" === typeof pattern) pattern = new RegExp(pattern);

  // call replacement function if needed
  if ("function" === typeof replacement) return replacement(input, pattern);

  // do a straight replace
  return input.replace(pattern, replacement);
};

/**
 * Removes common words from a slug.
 *
 * @param {String} slug
 * @returns {String}
 */
export const _removeStopwords = (slug) => {
  return _.difference(slug.split(SINGLE_HYPHEN), STOPWORDS).join(SINGLE_HYPHEN);
};

export const errorHandler = (result) => {
  const message = errorParser(result.xhr);
  const type = result.status === "error" ? "danger" : "warning";
  const autoDismiss = false;
  displayAlert({ type, message, autoDismiss });
};

export const displayAlert = ({
  type = "warning",
  message = "",
  autoDismiss = true,
} = {}) => {
  const channel = Radio.channel("global");
  channel.trigger("status:show", { type, message, autoDismiss });
};

export const errorParser = (jqXHR) => {
  let msg = "An error occurred while processing your request.";
  // http://www.w3.org/Protocols/rfc2616/rfc2616-sec10.html
  switch (jqXHR.status) {
    case 400:
      msg = "Your request could not be processed.";
      break;
    case 401:
      msg = "You are not authorized to make this request.";
      break;
    case 403:
      msg = "You are not authorized to access this resource.";
      break;
    case 404:
      msg = "The requested resource was not found.";
      break;
    case 408:
      msg = "Your request has timed out.";
      break;
    case 409:
      msg = "Your request attempted to duplicate an existing resource.";
      break;
    case 410:
      msg = "The requested resource is no longer available.";
      break;
    case 500:
      msg = "An internal error occurred.";
      break;
    case 501:
      msg = "Your request is not supported.";
      break;
    case 503:
      msg = "The service is unavailable. Please try again later.";
      break;
  }
  return msg;
};

/* https://davidwalsh.name/css-animation-callback */
export const getTransitionEvent = (el) => {
  const transitions = {
    transition: "transitionend",
    OTransition: "oTransitionEnd",
    MozTransition: "transitionend",
    WebkitTransition: "webkitTransitionEnd",
  };

  /*eslint no-unused-vars: "warn"*/
  for (const t in transitions) {
    if (el.style[t] !== undefined) {
      return transitions[t];
    }
  }
};

/**
 * Returns version of IE or false, if browser is not Internet Explorer
 * See https://www.npmjs.com/package/platform
 */
export const detectIE = (userAgent = window.navigator.userAgent) => {
  const p = platform.parse(userAgent);

  if (p.layout.toLowerCase() === "trident") {
    return +p.version || p.version;
  }

  return false;
};

/**
 * Use this as a callback to events when you need to suspend an HTML
 * component"s default behavior.
 *
 * @param {Event} e
 * @returns {void}
 */
export const preventDefault = (e) => {
  e.preventDefault();
};

/**
 * Does nothing, use it as a placeholder in callbacks
 *
 * @returns {void}
 */
export const emptyFn = () => {};
